import React, { useEffect, useRef, forwardRef } from "react";
import styled from "styled-components";

/**
 * Represents an ambient element.
 */
class AmbientElement {
	constructor(container, attributes) {
		this.name = container.getAttribute("id") || undefined;
		this.originalAttributes = attributes || [];
		this.container = container;
		this._shadowElement = this.container.querySelector(".ambient__shadow");
	}

	disable() {
		if (this._shadowElement.style.display !== "none") {
			this._shadowElement.style.display = "none";
		}
	}

	enable() {
		if (this._shadowElement.style.display !== "block") {
			this._shadowElement.style.display = "block";
		}
	}
}

/**
 * Represents the Ambient class.
 */
class Ambient {
	constructor(options = {}) {
		this._customBlur =
			options.blur >= 0 && options.blur !== 45 ? options.blur : null;
		this._retainAttributes = options.retainAttributes || false;

		this.options = {
			insertCSS: true,
			retainAttributes: false,
			blur: -1,
			...options,
		};

		if (this.options.insertCSS) {
			this.applyDefaultStyles();
		}

		this.elements = [];
	}

	mount(element) {
		const images = element.querySelectorAll("img[data-ambient]");
		images.forEach((img) => this.elements.push(this.renderImage(img)));

		const videos = element.querySelectorAll("video[data-ambient]");
		videos.forEach((video) => this.elements.push(this.renderVideo(video)));

		return this.elements;
	}

	unmount() {
		this.elements.forEach((ambientElement) => {
			const container = ambientElement.container;
			const visibleElement = container.querySelector(".ambient__visible");
			visibleElement.classList.remove("ambient__visible");
			visibleElement.setAttribute(
				"data-ambient",
				ambientElement.name || ""
			);

			ambientElement.originalAttributes.forEach((attribute) => {
				if (
					!visibleElement.hasAttribute(attribute.name) ||
					visibleElement.getAttribute(attribute.name) !==
						attribute.value
				) {
					visibleElement.setAttribute(
						attribute.name,
						attribute.value
					);
				}
			});

			container.parentNode.insertBefore(visibleElement, container);
			container.parentNode.removeChild(container);
		});

		this.elements = [];
	}

	renderVideo(videoElement) {
		const container = document.createElement("div");
		container.className = "ambient__container";

		if (videoElement.getAttribute("data-ambient").length > 0) {
			container.id = videoElement.getAttribute("data-ambient");
		}

		const visibleElement = videoElement.cloneNode(true);
		const originalAttributes = [];

		if (!this._retainAttributes) {
			originalAttributes.push(...visibleElement.attributes);
			Array.from(visibleElement.attributes).forEach((attr) => {
				if (attr.name.toLowerCase() !== "src") {
					visibleElement.removeAttribute(attr.name);
				}
			});
		}

		visibleElement.classList.add("ambient__visible");

		const shadowElement = document.createElement("canvas");
		shadowElement.className = "ambient__shadow";

		if (this._customBlur) {
			this.applyBlur(shadowElement, this._customBlur);
		}

		visibleElement.removeAttribute("data-ambient");

		(function (video, canvas) {
			const context = canvas.getContext("2d");
			let shouldRequestNewFrame = false;

			function mirrorCanvas() {
				context.clearRect(0, 0, canvas.width, canvas.height);
				context.drawImage(video, 0, 0, canvas.width, canvas.height);

				if (shouldRequestNewFrame) {
					window.requestAnimationFrame(mirrorCanvas);
				}
			}

			video.onloadeddata = () => {
				window.requestAnimationFrame(mirrorCanvas);
			};

			video.onplay = () => {
				shouldRequestNewFrame = true;
				window.requestAnimationFrame(mirrorCanvas);
			};

			video.onpause = () => {
				shouldRequestNewFrame = false;
			};
			video.onabort = () => {
				shouldRequestNewFrame = false;
			};
		})(visibleElement, shadowElement);

		container.appendChild(visibleElement);
		container.appendChild(shadowElement);

		videoElement.parentNode.insertBefore(container, videoElement);
		videoElement.parentNode.removeChild(videoElement);

		return new AmbientElement(container, originalAttributes);
	}

	renderImage(imgElement) {
		const container = document.createElement("div");
		container.className = "ambient__container";

		if (imgElement.getAttribute("data-ambient").length > 0) {
			container.id = imgElement.getAttribute("data-ambient");
		}

		let visibleElement;
		const originalAttributes = [];

		if (this._retainAttributes) {
			visibleElement = imgElement.cloneNode();
		} else {
			originalAttributes.push(...imgElement.attributes);
			visibleElement = document.createElement("img");
			visibleElement.src = imgElement.src;
			visibleElement.alt = imgElement.alt;
		}
		
		visibleElement.classList = imgElement.classList;
		visibleElement.classList.add("ambient__visible");

		const shadowElement = document.createElement("img");
		shadowElement.classList = imgElement.classList;
		shadowElement.classList.add("ambient__shadow");
		shadowElement.src = visibleElement.src;

		if (this._customBlur) {
			this.applyBlur(shadowElement, this._customBlur);
		}

		visibleElement.removeAttribute("data-ambient");
		shadowElement.removeAttribute("data-ambient");

		visibleElement.onload = () => {
			shadowElement.src = visibleElement.src;
		};

		container.appendChild(visibleElement);
		container.appendChild(shadowElement);

		imgElement.parentNode.insertBefore(container, imgElement);
		imgElement.parentNode.removeChild(imgElement);

		return new AmbientElement(container, originalAttributes);
	}

	applyBlur(element, value) {
		const blur = `blur(${value}px)`;
		element.style.filter = blur;
		element.style.webkitFilter = blur;
		element.style.mozFilter = blur;
		element.style.msFilter = blur;
	}

	applyDefaultStyles() {
		const defaultStyles =
			"LmFtYmllbnRfX2NvbnRhaW5lcntwb3NpdGlvbjpyZWxhdGl2ZX0uYW1iaWVudF9fdmlzaWJsZXtkaXNwbGF5OmJsb2NrO3Bvc2l0aW9uOnJlbGF0aXZlO3otaW5kZXg6MTA7d2lkdGg6MTAwJX0uYW1iaWVudF9fc2hhZG93e2Rpc3BsYXk6YmxvY2s7cG9zaXRpb246YWJzb2x1dGU7d2lkdGg6MTAwJTtoZWlnaHQ6MTAwJTt6LWluZGV4OjU7bGVmdDowO3RvcDowOy13ZWJraXQtZmlsdGVyOmJsdXIoNDVweCk7LW1vei1maWx0ZXI6Ymx1cig0NXB4KTstbXMtZmlsdGVyOmJsdXIoNDVweCk7ZmlsdGVyOmJsdXIoNDVweCl9";
		const styleEl = document.createElement("style");
		styleEl.textContent = atob(defaultStyles);
		document.querySelector("head").appendChild(styleEl);
	}
}

/**
 * AmbientImage - A React component that applies ambient effects to an image.
 * @param {Object} options - The options for the Ambient instance.
 * @param {string} src - The source of the image.
 * @param {string} alt - The alt text for the image.
 * @param {string} className - The class name for styling.
 * @returns {JSX.Element} The rendered component.
 */
const AmbientImage = forwardRef(({ options, src, alt, className }, ref) => {
	const containerRef = useRef(null);

	useEffect(() => {
		const ambient = new Ambient(options);
		ambient.mount(containerRef.current);

		return () => {
			ambient.unmount();
		};
	}, [options]);

	return (
		<div ref={containerRef}>
			<img
				ref={ref}
				src={src}
				alt={alt}
				data-ambient
				className={className}
			/>
		</div>
	);
});

export default AmbientImage;
